import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Container,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import Navbar from "../components/Navbar";
import { ARAGON_FONTS, AragonFontLoader, aragonHomeTheme } from "../theme/AragonTheme";

const HERO_VIDEO_SRC = "/images/Video Project 2_compressed.mp4";
const LOGO_SRC = "/images/1080 x 1080 px Logo 4.png";
const SELLER_CTA_IMAGE = "/images/Call 2 Action - Seller.jpg";
const BUYER_CTA_IMAGE = "/images/Call 2 Action - Buyer.jpg";

const COLORS = {
  navy: "#000D38",
  navySoft: "#0D1737",
  warmWhite: "#F7F6F5",
  pale: "#EDEFEC",
  white: "#FFFFFF",
  warmBlack: "#141414",
  muted: "#696B70",
  mutedLight: "#AEB5C4",
  accent: "#CCA074",
  accentSoft: "#E5C6A6",
  wine: "#3D1F28",
  line: "rgba(20,20,20,0.12)",
  lightLine: "rgba(255,255,255,0.16)",
  success: "#3CA36C",
};

const metrics = [
  {
    value: "15×",
    label: "More Buyers",
    body: "Broader qualified buyer coverage around each opportunity.",
  },
  {
    value: "30%",
    label: "Higher",
    body: "More competitive offer tension through structured matching.",
  },
  {
    value: "<45d",
    label: "Faster",
    body: "Move from preparation to meaningful buyer engagement sooner.",
  },
  {
    value: "Zero",
    label: "Upfront Fees",
    body: "Start the process without a heavy traditional retainer structure.",
  },
];

const deals = [
  { sector: "Industrial Services", market: "Vietnam", value: "VND 86B", status: "Valuation complete" },
  { sector: "Healthcare Services", market: "Singapore", value: "VND 124B", status: "Buyer matching" },
  { sector: "Business Services", market: "Vietnam", value: "VND 61B", status: "In review" },
  { sector: "Consumer", market: "Thailand", value: "VND 97B", status: "Valuation complete" },
  { sector: "Manufacturing", market: "Vietnam", value: "VND 152B", status: "Buyer matching" },
  { sector: "Technology", market: "Malaysia", value: "VND 73B", status: "In review" },
];

const sellerJourney = [
  {
    step: "01",
    label: "Prepare",
    title: "Bring the business into transaction-ready form",
    body: "Turn scattered company information into one controlled deal record.",
    artifact: {
      type: "assemble",
      items: ["Profile", "Financials", "Operations", "Objectives"],
    },
  },
  {
    step: "02",
    label: "Value",
    title: "Understand value before entering the market",
    body: "Frame an indicative enterprise-value range against relevant market comparables.",
    artifact: {
      type: "valuation",
      low: "126B",
      central: "152B",
      high: "181B",
      comparables: 20,
    },
  },
  {
    step: "03",
    label: "Position",
    title: "Show buyers what makes the business investable",
    body: "Surface the operating qualities that shape buyer conviction before outreach.",
    artifact: {
      type: "radar",
      labels: ["Market", "Margins", "Scale", "Resilience"],
      values: [0.86, 0.79, 0.91, 0.68],
    },
  },
  {
    step: "04",
    label: "Match",
    title: "Prioritize buyers whose acquisition thesis fits",
    body: "Move from a broad market universe to counterparties with stronger strategic relevance.",
    artifact: {
      type: "network",
      nodes: 18,
      highlighted: 6,
    },
  },
  {
    step: "05",
    label: "Execute",
    title: "Advance the deal without losing transaction context",
    body: "Keep each decision point visible as interest progresses toward a transaction.",
    artifact: {
      type: "timeline",
      stages: ["Prep", "NDA", "Meeting", "IOI", "LOI"],
      active: 3,
    },
  },
];

const buyerJourney = [
  {
    step: "01",
    label: "Define",
    title: "Turn acquisition intent into a clear mandate",
    body: "Set the acquisition lens before opportunities compete for attention.",
    artifact: {
      type: "lens",
      labels: ["Sector", "Model", "Market", "Fit"],
    },
  },
  {
    step: "02",
    label: "Discover",
    title: "Surface businesses aligned with the mandate",
    body: "Narrow a broad universe around business-model and strategic relevance.",
    artifact: {
      type: "funnel",
      labels: ["Market", "Relevant", "Strong fit", "Priority"],
      values: [1258, 126, 38, 12],
    },
  },
  {
    step: "03",
    label: "Evaluate",
    title: "Understand the opportunity before committing resources",
    body: "Bring strategic fit, operating evidence and valuation context into one view.",
    artifact: {
      type: "radar",
      labels: ["Model", "Sector", "Ops", "Value"],
      values: [0.92, 0.88, 0.69, 0.81],
    },
  },
  {
    step: "04",
    label: "Compare",
    title: "Compare opportunities on one investment lens",
    body: "See the shortlist side by side without turning the homepage into a data table.",
    artifact: {
      type: "compare",
      candidates: [
        { name: "A", fit: 0.91, multiple: "7.8x" },
        { name: "B", fit: 0.78, multiple: "9.1x" },
        { name: "C", fit: 0.64, multiple: "6.9x" },
      ],
    },
  },
  {
    step: "05",
    label: "Execute",
    title: "Move from interest to a controlled acquisition process",
    body: "Carry opportunity context forward as the process moves into meetings and diligence.",
    artifact: {
      type: "timeline",
      stages: ["Review", "NDA", "Meeting", "Offer", "DD"],
      active: 3,
    },
  },
];

const sectors = [
  {
    title: "Home Services",
    description: "HVAC · Plumbing · Electrical · Landscaping",
    eyebrow: "Field Services",
    images: [
      {
        src: "/images/sectors/home-services/electrician.jpg",
        remote:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0f/Electrician_at_work%2C_Goa%2C_India._2.jpg/1280px-Electrician_at_work%2C_Goa%2C_India._2.jpg",
      },
      {
        src: "/images/sectors/home-services/plumbing.jpg",
        remote:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/1/12/Plumbing_and_Gas_Technician.jpg/1280px-Plumbing_and_Gas_Technician.jpg",
      },
      {
        src: "/images/sectors/home-services/hvac.jpg",
        remote:
          "https://upload.wikimedia.org/wikipedia/commons/c/cd/Air_conditioning_and_refrigeration_repair.jpg",
      },
    ],
    fallbacks: ["#567B83", "#9A654C", "#526B74"],
  },
  {
    title: "Business Services",
    description: "Professional Services · Facilities · Outsourcing · Staffing",
    eyebrow: "Business Operations",
    images: [
      {
        src: "/images/sectors/business-services/team-meeting.jpg",
        remote:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/6/62/Team_Meeting.jpg/1280px-Team_Meeting.jpg",
      },
      {
        src: "/images/sectors/business-services/business-meeting.jpg",
        remote:
          "https://upload.wikimedia.org/wikipedia/commons/e/e1/Businessmeeting.jpg",
      },
      {
        src: "/images/sectors/business-services/leadership-meeting.jpg",
        remote:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d1/Meeting_CEO_with_Staff.jpg/1280px-Meeting_CEO_with_Staff.jpg",
      },
    ],
    fallbacks: ["#485B7D", "#7B6659", "#5D6D72"],
  },
  {
    title: "Industrials",
    description: "Manufacturing · Distribution · Logistics · Specialty Products",
    eyebrow: "Industrial Operations",
    images: [
      {
        src: "/images/sectors/industrials/automation.jpg",
        remote:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/2/23/Manufacturing_equipment_109.jpg/1280px-Manufacturing_equipment_109.jpg",
      },
      {
        src: "/images/sectors/industrials/machine-operator.jpg",
        remote:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e1/Worker_operates_machinery_in_a_factory.jpg/1280px-Worker_operates_machinery_in_a_factory.jpg",
      },
      {
        src: "/images/sectors/industrials/garment-factory.jpg",
        remote:
          "https://upload.wikimedia.org/wikipedia/commons/2/26/Clothing_Factory_Workers_at_Thuan_Phuong_Garment_Company.jpg",
      },
    ],
    fallbacks: ["#285C65", "#A1543F", "#31425B"],
  },
  {
    title: "Healthcare",
    description: "Clinical Services · Dental · Home Care · Healthcare Support",
    eyebrow: "Healthcare Services",
    images: [
      {
        src: "/images/sectors/healthcare/nurses.jpg",
        remote:
          "https://upload.wikimedia.org/wikipedia/commons/7/71/Nurses_at_Hospital.jpg",
      },
      {
        src: "/images/sectors/healthcare/clinical-team.jpg",
        remote:
          "https://upload.wikimedia.org/wikipedia/commons/d/db/Doctors_and_Nurses_%2844601738131%29.jpg",
      },
      {
        src: "/images/sectors/healthcare/hospital-team.jpg",
        remote:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/1/16/Doctors_and_nurses_at_the_3rd_Field_Hospital.jpg/1280px-Doctors_and_nurses_at_the_3rd_Field_Hospital.jpg",
      },
    ],
    fallbacks: ["#5D7D83", "#927264", "#516D78"],
  },
  {
    title: "Consumer",
    description: "Retail · Food & Beverage · Consumer Services · Lifestyle",
    eyebrow: "Consumer Markets",
    images: [
      {
        src: "/images/sectors/consumer/retail-store.jpg",
        remote:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/6/6c/Dhaka_retail_store_and_customers.jpg/1280px-Dhaka_retail_store_and_customers.jpg",
      },
      {
        src: "/images/sectors/consumer/restaurant-workers.jpg",
        remote:
          "https://upload.wikimedia.org/wikipedia/commons/7/76/Restaurant_Workers_%28Unsplash%29.jpg",
      },
      {
        src: "/images/sectors/consumer/supermarket.jpg",
        remote:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/4/45/Save-On-Foods_Saskatoon_aisles.jpg/1280px-Save-On-Foods_Saskatoon_aisles.jpg",
      },
    ],
    fallbacks: ["#9B6246", "#B56A46", "#3C6871"],
  },
];

const faqs = [
  {
    question: "What size businesses is this designed for?",
    answer:
      "This mock experience is designed around established small and lower-middle-market businesses where structured valuation and buyer discovery can materially improve decision quality.",
  },
  {
    question: "How does the buyer process work?",
    answer:
      "Business profile, valuation context and buyer criteria are brought into one flow so opportunities can be screened, compared and progressed in a controlled way.",
  },
  {
    question: "Do I need to be ready to sell immediately?",
    answer:
      "No. An owner can first understand valuation, market position and likely buyer interest before deciding whether to start a transaction process.",
  },
  {
    question: "How is confidentiality handled?",
    answer:
      "The production version should disclose information progressively and only expose deeper evidence when the counterparty and transaction stage justify it.",
  },
  {
    question: "What happens after I submit my website?",
    answer:
      "In this visual mock the control is non-functional. Once connected to the backend it can become the first step into the business intake and valuation workflow.",
  },
];

function useViewportModel() {
  const [model, setModel] = useState(() => ({
    width: typeof window === "undefined" ? 1440 : window.innerWidth,
    height: typeof window === "undefined" ? 900 : window.innerHeight,
  }));
  useLayoutEffect(() => {
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const viewport = window.visualViewport;
        setModel({
          width: Math.round(viewport?.width || window.innerWidth),
          height: Math.round(viewport?.height || window.innerHeight),
        });
      });
    };
    update();
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
    };
  }, []);
  return useMemo(() => {
    const widthScale = d3.scaleLinear().domain([360, 1920]).range([0.82, 1.08]).clamp(true);
    const heightScale = d3.scaleLinear().domain([620, 1080]).range([0.78, 1]).clamp(true);
    const gutterScale = d3.scaleLinear().domain([360, 1920]).range([18, 68]).clamp(true);
    const maxWidthScale = d3.scaleLinear().domain([900, 1920]).range([920, 1480]).clamp(true);
    const sectionScale = d3.scaleLinear().domain([620, 1080]).range([70, 126]).clamp(true);
    const heroTitleScale = d3.scaleLinear().domain([360, 1920]).range([44, 82]).clamp(true);
    const sectionTitleScale = d3.scaleLinear().domain([360, 1920]).range([38, 62]).clamp(true);
    const navbarHeightFactor = d3.scaleLinear().domain([600, 1080]).range([0.88, 1]).clamp(true)(model.height);
    const navbarHeight =
      d3.scaleLinear().domain([360, 1920]).range([64, 82]).clamp(true)(model.width) * navbarHeightFactor;
    const combined = Math.min(widthScale(model.width), heightScale(model.height));
    const heroAspect = model.width / Math.max(1, model.height);
    const heroVideoScale = d3.scaleLinear().domain([1.78, 2.25]).range([1.265, 1]).clamp(true)(heroAspect);
    return {
      ...model,
      compact: model.width < 760,
      tablet: model.width < 1080,
      shortViewport: model.height < 720,
      combined,
      gutter: Math.round(gutterScale(model.width)),
      maxWidth: Math.round(maxWidthScale(model.width)),
      sectionY: Math.round(sectionScale(model.height)),
      heroTitle: Math.round(heroTitleScale(model.width) * Math.max(0.84, combined)),
      sectionTitle: Math.round(sectionTitleScale(model.width) * Math.max(0.92, combined)),
      heroBody: Math.round(d3.scaleLinear().domain([360, 1600]).range([15, 18]).clamp(true)(model.width)),
      ctaHeight: Math.round(d3.scaleLinear().domain([360, 1600]).range([44, 50]).clamp(true)(model.width)),
      ctaRadius: Math.round(d3.scaleLinear().domain([360, 1600]).range([10, 14]).clamp(true)(model.width)),
      ctaPad: Math.round(d3.scaleLinear().domain([360, 1600]).range([4, 6]).clamp(true)(model.width)),
      ctaButtonWidth: Math.round(d3.scaleLinear().domain([360, 1600]).range([118, 148]).clamp(true)(model.width)),
      ctaFont: d3.scaleLinear().domain([360, 1600]).range([13, 14]).clamp(true)(model.width),
      intelligenceCardHeight: Math.round(d3.scaleLinear().domain([360, 900]).range([260, 330]).clamp(true)(model.width)),
      intelligenceCardPadding: Math.round(d3.scaleLinear().domain([360, 900]).range([24, 32]).clamp(true)(model.width)),
      intelligenceCardTitle: Math.round(d3.scaleLinear().domain([360, 900]).range([28, 34]).clamp(true)(model.width)),
      intelligenceImageScale: d3.scaleLinear().domain([360, 1200]).range([1.08, 1.02]).clamp(true)(model.width),
      heroVideoScale,
      navClearance: Math.round(
        navbarHeight + d3.scaleLinear().domain([620, 1080]).range([12, 22]).clamp(true)(model.height)
      ),
      heroGap: Math.round(d3.scaleLinear().domain([620, 1080]).range([12, 28]).clamp(true)(model.height)),
      metricPad: Math.round(d3.scaleLinear().domain([620, 1080]).range([9, 20]).clamp(true)(model.height)),
      tickerHeight: Math.round(d3.scaleLinear().domain([620, 1080]).range([62, 78]).clamp(true)(model.height)),
      tickerLabelWidth: Math.round(d3.scaleLinear().domain([360, 1920]).range([116, 190]).clamp(true)(model.width)),
      tickerDealWidth: Math.round(d3.scaleLinear().domain([360, 1920]).range([270, 390]).clamp(true)(model.width)),
    };
  }, [model]);
}

function Reveal({ children, delay = 0, y = 24, sx = {} }) {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return undefined;
    }
    d3.select(node).style("opacity", 0).style("transform", `translateY(${y}px)`);
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          return;
        }
        d3.select(node)
          .transition()
          .delay(delay)
          .duration(620)
          .ease(d3.easeCubicOut)
          .style("opacity", 1)
          .style("transform", "translateY(0px)");
        observer.disconnect();
      },
      { threshold: 0.16 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [delay, y]);
  return (
    <Box ref={ref} sx={sx}>
      {children}
    </Box>
  );
}

function DealTicker({ viewport }) {
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const pausedRef = useRef(false);
  useEffect(() => {
    const viewportNode = viewportRef.current;
    const trackNode = trackRef.current;
    if (!viewportNode || !trackNode) {
      return undefined;
    }
    let offset = 0;
    let previousElapsed = 0;
    let oneSetWidth = trackNode.scrollWidth / 2;
    const speed = d3.scaleLinear().domain([360, 1920]).range([0.020, 0.040]).clamp(true)(viewport.width);
    const resizeObserver = new ResizeObserver(() => {
      oneSetWidth = trackNode.scrollWidth / 2;
      if (oneSetWidth > 0) {
        offset %= oneSetWidth;
      }
    });
    resizeObserver.observe(trackNode);
    const timer = d3.timer((elapsed) => {
      const delta = Math.max(0, elapsed - previousElapsed);
      previousElapsed = elapsed;
      if (pausedRef.current || oneSetWidth <= 0) {
        return;
      }
      offset = (offset + delta * speed) % oneSetWidth;
      d3.select(trackNode).style("transform", `translate3d(${-offset}px,0,0)`);
    });
    return () => {
      resizeObserver.disconnect();
      timer.stop();
    };
  }, [viewport.width]);
  const repeated = [...deals, ...deals];
  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        zIndex: 3,
        height: "100%",
        minHeight: 0,
        display: "grid",
        gridTemplateColumns: `${viewport.tickerLabelWidth}px minmax(0, 1fr)`,
        alignItems: "stretch",
        bgcolor: "rgba(247,246,245,0.96)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        borderTop: "1px solid rgba(204,160,116,0.48)",
        color: COLORS.warmBlack,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: { xs: 1.6, md: 2.4 },
          borderRight: `1px solid ${COLORS.line}`,
          bgcolor: "rgba(247,246,245,0.78)",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: ARAGON_FONTS.secondary,
              fontSize: { xs: 9.5, md: 10.5 },
              fontWeight: 800,
              letterSpacing: "0.11em",
              color: COLORS.navy,
              textTransform: "uppercase",
              whiteSpace: "nowrap",
            }}
          >
            Currently
          </Typography>
          <Typography
            sx={{
              mt: 0.2,
              fontFamily: ARAGON_FONTS.primary,
              fontSize: { xs: 14, md: 17 },
              lineHeight: 1,
              color: COLORS.navy,
              whiteSpace: "nowrap",
            }}
          >
            Selling
          </Typography>
        </Box>
      </Box>
      <Box
        ref={viewportRef}
        onMouseEnter={() => {
          pausedRef.current = true;
        }}
        onMouseLeave={() => {
          pausedRef.current = false;
        }}
        sx={{
          minWidth: 0,
          height: "100%",
          overflow: "hidden",
          cursor: "default",
        }}
      >
        <Stack
          ref={trackRef}
          direction="row"
          alignItems="stretch"
          sx={{
            width: "max-content",
            height: "100%",
            willChange: "transform",
          }}
        >
          {repeated.map((deal, index) => (
            <Stack
              key={`${deal.sector}-${index}`}
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{
                width: `${viewport.tickerDealWidth}px`,
                px: { xs: 2, md: 2.8 },
                flexShrink: 0,
                borderRight: `1px solid ${COLORS.line}`,
                transition: "background-color 150ms ease",
                "&:hover": {
                  bgcolor: "rgba(204,160,116,0.10)",
                },
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: index % 3 === 0 ? COLORS.success : COLORS.accent,
                  flexShrink: 0,
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={0.9} alignItems="baseline">
                  <Typography
                    sx={{
                      fontFamily: ARAGON_FONTS.secondary,
                      fontSize: { xs: 11.5, md: 12.5 },
                      fontWeight: 750,
                      color: COLORS.navy,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {deal.sector}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: ARAGON_FONTS.secondary,
                      fontSize: { xs: 9.5, md: 10.5 },
                      color: COLORS.muted,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {deal.market}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mt: 0.25 }}>
                  <Typography
                    sx={{
                      fontFamily: ARAGON_FONTS.secondary,
                      fontSize: { xs: 10.5, md: 11.5 },
                      color: COLORS.warmBlack,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {deal.value}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: ARAGON_FONTS.secondary,
                      fontSize: { xs: 9.5, md: 10.5 },
                      color: COLORS.muted,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {deal.status}
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}

function JourneyIllustration({ artifact, size }) {
  const svgRef = useRef(null);
  const width = Math.max(300, size.width);
  const height = Math.max(230, size.height);
  const pad = d3.scaleLinear().domain([300, 820]).range([22, 42]).clamp(true)(width);
  const textSmall = d3.scaleLinear().domain([300, 820]).range([9, 11]).clamp(true)(width);
  const textMedium = d3.scaleLinear().domain([300, 820]).range([12, 15]).clamp(true)(width);
  useEffect(() => {
    const node = svgRef.current;
    if (!node) {
      return undefined;
    }
    const root = d3.select(node);
    root.selectAll("[data-visual-enter]").interrupt().style("opacity", 0).transition().duration(520).delay((_, index) => index * 42).ease(d3.easeCubicOut).style("opacity", 1);
    root.selectAll("[data-visual-draw]").each(function () {
      const path = d3.select(this);
      const length = this.getTotalLength?.() || 0;
      if (!length) {
        return;
      }
      path.interrupt().attr("stroke-dasharray", `${length} ${length}`).attr("stroke-dashoffset", length).transition().duration(720).ease(d3.easeCubicOut).attr("stroke-dashoffset", 0);
    });
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) {
      return () => root.selectAll("*").interrupt();
    }
    const timer = d3.timer((elapsed) => {
      if (elapsed < 760 || document.visibilityState !== "visible") {
        return;
      }
      const motionTime = elapsed - 760;
      const wave = (Math.sin(motionTime / 620) + 1) / 2;
      if (artifact.type === "assemble") {
        const amplitude = 1.5 + wave * 3.5;
        root.selectAll("[data-motion-card]").attr("transform", function () {
          const index = Number(this.dataset.motionIndex || 0);
          const dx = index % 2 === 0 ? amplitude : -amplitude;
          const dy = index < 2 ? amplitude : -amplitude;
          return `translate(${dx},${dy})`;
        });
        root.select("[data-motion-core]").attr("r", function () {
          const base = Number(this.dataset.baseR || 1);
          return base * (0.96 + wave * 0.08);
        });
      }
      if (artifact.type === "valuation") {
        root.selectAll("[data-motion-comparable]").style("opacity", function () {
          const index = Number(this.dataset.motionIndex || 0);
          return 0.46 + ((Math.sin(motionTime / 440 + index * 0.62) + 1) / 2) * 0.54;
        });
        root.select("[data-motion-primary]").attr("r", 7 + wave * 2.1);
      }
      if (artifact.type === "radar") {
        const scale = 0.965 + wave * 0.035;
        root.select("[data-motion-radar]").attr("transform", `translate(${width / 2},${height / 2}) scale(${scale}) translate(${-width / 2},${-height / 2})`);
      }
      if (artifact.type === "network") {
        root.selectAll("[data-motion-network-node]").attr("r", function () {
          const base = Number(this.dataset.baseR || 4);
          const index = Number(this.dataset.motionIndex || 0);
          const localWave = (Math.sin(motionTime / 430 + index * 0.74) + 1) / 2;
          return base + localWave * 1.6;
        });
        root.select("[data-motion-network-core]").attr("r", function () {
          const base = Number(this.dataset.baseR || 1);
          return base * (0.95 + wave * 0.10);
        });
      }
      if (artifact.type === "timeline") {
        root.select("[data-motion-current]").attr("r", 13 + wave * 7).style("opacity", 0.34 + wave * 0.42);
      }
      if (artifact.type === "lens") {
        root.selectAll("[data-motion-ring]").attr("r", function () {
          const base = Number(this.dataset.baseR || 1);
          const index = Number(this.dataset.motionIndex || 0);
          return base + Math.sin(motionTime / 520 + index * 0.82) * 2.6;
        });
      }
      if (artifact.type === "funnel") {
        root.selectAll("[data-motion-funnel]").each(function () {
          const rect = d3.select(this);
          const base = Number(this.dataset.baseWidth || 1);
          const index = Number(this.dataset.motionIndex || 0);
          const localWave = (Math.sin(motionTime / 560 + index * 0.7) + 1) / 2;
          const currentWidth = base * (0.97 + localWave * 0.03);
          rect.attr("width", currentWidth).attr("x", (width - currentWidth) / 2);
        });
      }
      if (artifact.type === "compare") {
        root.selectAll("[data-motion-compare]").attr("width", function () {
          const base = Number(this.dataset.baseWidth || 1);
          const index = Number(this.dataset.motionIndex || 0);
          const localWave = (Math.sin(motionTime / 510 + index * 0.9) + 1) / 2;
          return base * (0.94 + localWave * 0.06);
        });
      }
    });
    return () => {
      timer.stop();
      root.selectAll("*").interrupt();
    };
  }, [artifact, height, width]);
  const commonText = { fontFamily: ARAGON_FONTS.secondary, fill: "rgba(255,255,255,0.64)" };
  const strongText = { fontFamily: ARAGON_FONTS.secondary, fill: COLORS.white };
  if (artifact.type === "assemble") {
    const center = { x: width / 2, y: height / 2 };
    const cardW = Math.min(width * 0.23, 128);
    const cardH = Math.min(height * 0.22, 74);
    const positions = [
      { x: pad + cardW / 2, y: pad + cardH / 2 },
      { x: width - pad - cardW / 2, y: pad + cardH / 2 },
      { x: pad + cardW / 2, y: height - pad - cardH / 2 },
      { x: width - pad - cardW / 2, y: height - pad - cardH / 2 },
    ];
    return (
      <Box ref={svgRef} component="svg" viewBox={`0 0 ${width} ${height}`} sx={{ width: "100%", height: "100%", display: "block" }}>
        {positions.map((position, index) => (
          <g key={artifact.items[index]} data-visual-enter data-motion-card data-motion-index={index}>
            <line data-visual-draw x1={position.x} y1={position.y} x2={center.x} y2={center.y} stroke="rgba(204,160,116,0.34)" strokeWidth="1.2" />
            <rect x={position.x - cardW / 2} y={position.y - cardH / 2} width={cardW} height={cardH} rx="4" fill="rgba(255,255,255,0.045)" stroke="rgba(255,255,255,0.17)" />
            <line x1={position.x - cardW * 0.28} y1={position.y - 8} x2={position.x + cardW * 0.22} y2={position.y - 8} stroke="rgba(255,255,255,0.28)" />
            <line x1={position.x - cardW * 0.28} y1={position.y + 1} x2={position.x + cardW * 0.10} y2={position.y + 1} stroke="rgba(255,255,255,0.18)" />
            <text x={position.x} y={position.y + cardH * 0.29} textAnchor="middle" fontSize={textSmall} fontWeight="700" {...commonText}>{artifact.items[index]}</text>
          </g>
        ))}
        <g data-visual-enter>
          <circle cx={center.x} cy={center.y} r={Math.min(width, height) * 0.115} fill="rgba(204,160,116,0.11)" stroke="rgba(204,160,116,0.52)" />
          <circle data-motion-core data-base-r={Math.min(width, height) * 0.075} cx={center.x} cy={center.y} r={Math.min(width, height) * 0.075} fill={COLORS.accent} opacity="0.9" />
          <text x={center.x} y={center.y + 4} textAnchor="middle" fontFamily={ARAGON_FONTS.secondary} fontSize={textSmall} fontWeight="800" letterSpacing="1.1" fill={COLORS.navy}>DEAL READY</text>
        </g>
      </Box>
    );
  }
  if (artifact.type === "valuation") {
    const x = d3.scaleLinear().domain([0, 1]).range([pad * 1.3, width - pad * 1.3]);
    const y = height * 0.67;
    const dots = d3.range(artifact.comparables).map((index) => ({ x: x((index + 0.5) / artifact.comparables), y: height * 0.31 + Math.sin(index * 1.73) * height * 0.055 }));
    return (
      <Box ref={svgRef} component="svg" viewBox={`0 0 ${width} ${height}`} sx={{ width: "100%", height: "100%", display: "block" }}>
        <text x={width / 2} y={height * 0.21} textAnchor="middle" fontFamily={ARAGON_FONTS.secondary} fontSize={textSmall} fontWeight="800" letterSpacing="1.5" fill={COLORS.accentSoft}>{artifact.comparables} RELEVANT COMPS</text>
        {dots.map((dot, index) => <circle key={index} data-visual-enter data-motion-comparable data-motion-index={index} cx={dot.x} cy={dot.y} r={index % 5 === 0 ? 3.4 : 2.3} fill={index % 5 === 0 ? COLORS.accent : "rgba(255,255,255,0.34)"} />)}
        <line data-visual-draw x1={x(0)} x2={x(1)} y1={y} y2={y} stroke="rgba(255,255,255,0.28)" strokeWidth="1.4" />
        <line x1={x(0)} x2={x(0)} y1={y - 8} y2={y + 8} stroke="rgba(255,255,255,0.48)" />
        <line x1={x(1)} x2={x(1)} y1={y - 8} y2={y + 8} stroke="rgba(255,255,255,0.48)" />
        <circle data-visual-enter data-motion-primary cx={x(0.52)} cy={y} r="7" fill={COLORS.accent} />
        <text x={x(0.52)} y={y - 26} textAnchor="middle" fontFamily={ARAGON_FONTS.primary} fontSize={d3.scaleLinear().domain([300, 820]).range([26, 40]).clamp(true)(width)} fill={COLORS.white}>{artifact.central}</text>
        <text x={x(0)} y={y + 30} textAnchor="start" fontSize={textSmall} {...commonText}>{artifact.low}</text>
        <text x={x(1)} y={y + 30} textAnchor="end" fontSize={textSmall} {...commonText}>{artifact.high}</text>
      </Box>
    );
  }
  if (artifact.type === "radar") {
    const center = [width / 2, height / 2];
    const radius = Math.min(width, height) * 0.31;
    const angles = artifact.labels.map((_, index) => -Math.PI / 2 + index * (Math.PI * 2 / artifact.labels.length));
    const pointsAt = (ratio) => angles.map((angle) => [center[0] + Math.cos(angle) * radius * ratio, center[1] + Math.sin(angle) * radius * ratio]);
    const line = d3.line().curve(d3.curveLinearClosed);
    const valuePoints = artifact.values.map((value, index) => [center[0] + Math.cos(angles[index]) * radius * value, center[1] + Math.sin(angles[index]) * radius * value]);
    return (
      <Box ref={svgRef} component="svg" viewBox={`0 0 ${width} ${height}`} sx={{ width: "100%", height: "100%", display: "block" }}>
        {[0.34, 0.67, 1].map((ratio) => <path key={ratio} d={line(pointsAt(ratio))} fill="none" stroke="rgba(255,255,255,0.12)" />)}
        {angles.map((angle, index) => <line key={artifact.labels[index]} x1={center[0]} y1={center[1]} x2={center[0] + Math.cos(angle) * radius} y2={center[1] + Math.sin(angle) * radius} stroke="rgba(255,255,255,0.11)" />)}
        <g data-motion-radar>
          <path data-visual-draw d={line(valuePoints)} fill="rgba(204,160,116,0.18)" stroke={COLORS.accentSoft} strokeWidth="2" />
          {valuePoints.map((point, index) => <circle key={artifact.labels[index]} data-visual-enter cx={point[0]} cy={point[1]} r="4.5" fill={COLORS.accent} />)}
        </g>
        {angles.map((angle, index) => {
          const lx = center[0] + Math.cos(angle) * radius * 1.18;
          const ly = center[1] + Math.sin(angle) * radius * 1.18 + 4;
          return <text key={artifact.labels[index]} x={lx} y={ly} textAnchor={Math.abs(Math.cos(angle)) < 0.2 ? "middle" : Math.cos(angle) > 0 ? "start" : "end"} fontSize={textSmall} fontWeight="700" {...commonText}>{artifact.labels[index]}</text>;
        })}
      </Box>
    );
  }
  if (artifact.type === "network") {
    const center = [width / 2, height / 2];
    const ring = Math.min(width, height) * 0.33;
    const nodes = d3.range(artifact.nodes).map((index) => {
      const ratio = index < artifact.nodes * 0.45 ? 0.62 : 1;
      const angle = -Math.PI / 2 + index * (Math.PI * 2 / artifact.nodes) + (index % 2) * 0.08;
      return { x: center[0] + Math.cos(angle) * ring * ratio, y: center[1] + Math.sin(angle) * ring * ratio, active: index < artifact.highlighted };
    });
    return (
      <Box ref={svgRef} component="svg" viewBox={`0 0 ${width} ${height}`} sx={{ width: "100%", height: "100%", display: "block" }}>
        {nodes.filter((node) => node.active).map((node, index) => <line key={`line-${index}`} data-visual-draw x1={center[0]} y1={center[1]} x2={node.x} y2={node.y} stroke="rgba(204,160,116,0.30)" strokeWidth="1.1" />)}
        <circle cx={center[0]} cy={center[1]} r={Math.min(width, height) * 0.13} fill="rgba(204,160,116,0.12)" stroke="rgba(204,160,116,0.46)" />
        <circle data-motion-network-core data-base-r={Math.min(width, height) * 0.075} cx={center[0]} cy={center[1]} r={Math.min(width, height) * 0.075} fill={COLORS.accent} />
        <text x={center[0]} y={center[1] + 4} textAnchor="middle" fontFamily={ARAGON_FONTS.secondary} fontSize={textSmall} fontWeight="850" letterSpacing="1.2" fill={COLORS.navy}>TARGET</text>
        {nodes.map((node, index) => <circle key={index} data-visual-enter data-motion-network-node={node.active ? "true" : undefined} data-motion-index={index} data-base-r={node.active ? 6 : 3.4} cx={node.x} cy={node.y} r={node.active ? 6 : 3.4} fill={node.active ? COLORS.accentSoft : "rgba(255,255,255,0.28)"} stroke={node.active ? "rgba(255,255,255,0.7)" : "none"} />)}
      </Box>
    );
  }
  if (artifact.type === "timeline") {
    const x = d3.scalePoint().domain(d3.range(artifact.stages.length)).range([pad * 1.2, width - pad * 1.2]);
    const y = height * 0.51;
    return (
      <Box ref={svgRef} component="svg" viewBox={`0 0 ${width} ${height}`} sx={{ width: "100%", height: "100%", display: "block" }}>
        <line data-visual-draw x1={x(0)} x2={x(artifact.stages.length - 1)} y1={y} y2={y} stroke="rgba(255,255,255,0.18)" strokeWidth="2" />
        <line data-visual-draw x1={x(0)} x2={x(artifact.active)} y1={y} y2={y} stroke={COLORS.accent} strokeWidth="3" />
        {artifact.stages.map((stage, index) => {
          const complete = index < artifact.active;
          const current = index === artifact.active;
          return (
            <g key={stage} data-visual-enter>
              {current && <circle data-motion-current cx={x(index)} cy={y} r="15" fill="rgba(204,160,116,0.12)" />}
              <circle cx={x(index)} cy={y} r={current ? 7 : 5} fill={complete || current ? COLORS.accent : COLORS.navySoft} stroke={complete || current ? COLORS.accentSoft : "rgba(255,255,255,0.28)"} strokeWidth="2" />
              <text x={x(index)} y={y + 31} textAnchor="middle" fontFamily={ARAGON_FONTS.secondary} fontSize={textSmall} fontWeight={current ? "800" : "650"} fill={current ? COLORS.accentSoft : "rgba(255,255,255,0.56)"}>{stage}</text>
            </g>
          );
        })}
      </Box>
    );
  }
  if (artifact.type === "lens") {
    const center = [width / 2, height / 2];
    const base = Math.min(width, height) * 0.13;
    return (
      <Box ref={svgRef} component="svg" viewBox={`0 0 ${width} ${height}`} sx={{ width: "100%", height: "100%", display: "block" }}>
        {artifact.labels.map((label, index) => {
          const radius = base * (1.25 + index * 0.62);
          return (
            <g key={label} data-visual-enter>
              <circle data-motion-ring data-motion-index={index} data-base-r={radius} cx={center[0]} cy={center[1]} r={radius} fill="none" stroke={index === artifact.labels.length - 1 ? "rgba(204,160,116,0.30)" : "rgba(255,255,255,0.13)"} strokeWidth={index === artifact.labels.length - 1 ? 1.5 : 1} />
              <text x={center[0] + radius * 0.74} y={center[1] - radius * 0.67} textAnchor="middle" fontSize={textSmall} fontWeight="700" {...commonText}>{label}</text>
            </g>
          );
        })}
        <circle data-visual-enter cx={center[0]} cy={center[1]} r={base * 0.72} fill={COLORS.accent} />
        <circle cx={center[0]} cy={center[1]} r={base * 0.32} fill={COLORS.navy} />
        <text x={center[0]} y={center[1] + 4} textAnchor="middle" fontFamily={ARAGON_FONTS.secondary} fontSize={textSmall} fontWeight="850" fill={COLORS.white}>FIT</text>
      </Box>
    );
  }
  if (artifact.type === "funnel") {
    const max = d3.max(artifact.values) || 1;
    const widthScale = d3.scaleSqrt().domain([0, max]).range([width * 0.20, width * 0.72]);
    const band = d3.scaleBand().domain(d3.range(artifact.values.length)).range([pad, height - pad]).padding(0.30);
    return (
      <Box ref={svgRef} component="svg" viewBox={`0 0 ${width} ${height}`} sx={{ width: "100%", height: "100%", display: "block" }}>
        {artifact.values.map((value, index) => {
          const w = widthScale(value);
          const y = band(index);
          return (
            <g key={artifact.labels[index]} data-visual-enter>
              <rect data-motion-funnel data-motion-index={index} data-base-width={w} x={(width - w) / 2} y={y} width={w} height={band.bandwidth()} rx="3" fill={index === artifact.values.length - 1 ? "rgba(204,160,116,0.72)" : `rgba(255,255,255,${0.05 + index * 0.035})`} stroke={index === artifact.values.length - 1 ? COLORS.accentSoft : "rgba(255,255,255,0.13)"} />
              <text x={width / 2 - w / 2 + 12} y={y + band.bandwidth() / 2 + 4} fontSize={textSmall} fontWeight="700" {...commonText}>{artifact.labels[index]}</text>
              <text x={width / 2 + w / 2 - 12} y={y + band.bandwidth() / 2 + 4} textAnchor="end" fontSize={textMedium} fontWeight="800" {...strongText}>{value.toLocaleString()}</text>
            </g>
          );
        })}
      </Box>
    );
  }
  if (artifact.type === "compare") {
    const cardGap = d3.scaleLinear().domain([300, 820]).range([12, 22]).clamp(true)(width);
    const cardWidth = Math.min((width - pad * 2 - cardGap * 2) / 3, 160);
    const startX = (width - (cardWidth * 3 + cardGap * 2)) / 2;
    const barScale = d3.scaleLinear().domain([0, 1]).range([0, cardWidth - 26]);
    return (
      <Box ref={svgRef} component="svg" viewBox={`0 0 ${width} ${height}`} sx={{ width: "100%", height: "100%", display: "block" }}>
        {artifact.candidates.map((candidate, index) => {
          const x = startX + index * (cardWidth + cardGap);
          const y = height * 0.21 + (index === 0 ? -10 : index === 2 ? 10 : 0);
          const cardHeight = height * 0.58;
          return (
            <g key={candidate.name} data-visual-enter>
              <rect x={x} y={y} width={cardWidth} height={cardHeight} rx="4" fill={index === 0 ? "rgba(204,160,116,0.12)" : "rgba(255,255,255,0.04)"} stroke={index === 0 ? "rgba(204,160,116,0.54)" : "rgba(255,255,255,0.14)"} />
              <text x={x + 14} y={y + 26} fontFamily={ARAGON_FONTS.primary} fontSize={22} fill={index === 0 ? COLORS.accentSoft : COLORS.white}>{candidate.name}</text>
              <rect x={x + 13} y={y + cardHeight * 0.45} width={cardWidth - 26} height="5" rx="2.5" fill="rgba(255,255,255,0.12)" />
              <rect data-motion-compare data-motion-index={index} data-base-width={barScale(candidate.fit)} x={x + 13} y={y + cardHeight * 0.45} width={barScale(candidate.fit)} height="5" rx="2.5" fill={index === 0 ? COLORS.accent : "rgba(255,255,255,0.58)"} />
              <text x={x + 13} y={y + cardHeight * 0.45 - 10} fontSize={textSmall} fontWeight="700" {...commonText}>FIT {Math.round(candidate.fit * 100)}</text>
              <text x={x + 13} y={y + cardHeight - 22} fontSize={textMedium} fontWeight="800" {...strongText}>{candidate.multiple}</text>
            </g>
          );
        })}
      </Box>
    );
  }
  return null;
}
function DealJourneySlideshow({ journey, audience }) {
  const wrapRef = useRef(null);
  const contentRef = useRef(null);
  const progressRef = useRef(null);
  const timeoutRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [cycleKey, setCycleKey] = useState(0);
  const [size, setSize] = useState({ width: 1080, height: 470 });
  const autoplayMs = 6400;
  useLayoutEffect(() => {
    const node = wrapRef.current;
    if (!node) {
      return undefined;
    }
    const observer = new ResizeObserver(([entry]) => {
      const width = Math.max(280, entry.contentRect.width);
      const height = d3.scaleLinear().domain([320, 1480]).range([590, 470]).clamp(true)(width);
      setSize({ width, height: Math.round(height) });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const clearTimer = () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
    const schedule = () => {
      clearTimer();
      if (reducedMotion || document.visibilityState !== "visible") {
        return;
      }
      timeoutRef.current = window.setTimeout(() => {
        timeoutRef.current = null;
        setActiveIndex((current) => (current + 1) % journey.length);
      }, autoplayMs);
    };
    const handleVisibility = () => {
      clearTimer();
      if (document.visibilityState === "visible") {
        setCycleKey((current) => current + 1);
      }
    };
    schedule();
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      clearTimer();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [activeIndex, cycleKey, journey.length]);
  useEffect(() => {
    const node = contentRef.current;
    if (!node) {
      return;
    }
    d3.select(node)
      .interrupt()
      .style("opacity", 0.18)
      .style("transform", "translate3d(14px,0,0)")
      .transition()
      .duration(500)
      .ease(d3.easeCubicOut)
      .style("opacity", 1)
      .style("transform", "translate3d(0,0,0)");
    const progress = progressRef.current;
    if (progress && document.visibilityState === "visible") {
      d3.select(progress).interrupt().style("transform", "scaleX(0)").transition().duration(autoplayMs - 180).ease(d3.easeLinear).style("transform", "scaleX(1)");
    }
  }, [activeIndex, cycleKey]);
  const activateStep = (index) => {
    setActiveIndex(index);
    setCycleKey((current) => current + 1);
  };
  const active = journey[activeIndex];
  const stacked = size.width < 820;
  const contentPadding = d3.scaleLinear().domain([320, 1480]).range([20, 44]).clamp(true)(size.width);
  const titleSize = d3.scaleLinear().domain([320, 1480]).range([27, 40]).clamp(true)(size.width);
  const bodySize = d3.scaleLinear().domain([320, 1480]).range([13, 15]).clamp(true)(size.width);
  const stepNumberSize = d3.scaleLinear().domain([320, 1480]).range([10, 12]).clamp(true)(size.width);
  const stepLabelSize = d3.scaleLinear().domain([320, 1480]).range([12.8, 15.5]).clamp(true)(size.width);
  const artifactHeight = stacked
    ? d3.scaleLinear().domain([320, 820]).range([250, 305]).clamp(true)(size.width)
    : d3.scaleLinear().domain([820, 1480]).range([360, 405]).clamp(true)(size.width);
  const illustrationWidth = stacked ? Math.max(300, size.width - contentPadding * 1.64) : Math.max(300, size.width * 0.53 - contentPadding * 2);
  const illustrationHeight = Math.max(230, artifactHeight - contentPadding * (stacked ? 0.55 : 0.85));
  return (
    <Box
      ref={wrapRef}
      sx={{
        position: "relative",
        minHeight: `${size.height}px`,
        bgcolor: COLORS.white,
        border: `1px solid ${COLORS.line}`,
        overflow: "hidden",
      }}
    >
      <Box ref={contentRef} sx={{ display: "grid", gridTemplateColumns: stacked ? "1fr" : "0.92fr 1.08fr", minHeight: `${size.height - 78}px`, willChange: "transform, opacity" }}>
        <Stack sx={{ p: `${contentPadding}px`, justifyContent: "flex-start", borderRight: stacked ? "none" : `1px solid ${COLORS.line}`, borderBottom: stacked ? `1px solid ${COLORS.line}` : "none" }}>
          <Box>
            <Typography sx={{ mt: 2.1, fontFamily: ARAGON_FONTS.primary, fontSize: `${titleSize}px`, lineHeight: 1.04, letterSpacing: "-0.025em", color: COLORS.navy, maxWidth: 560 }}>
              {active.title}
            </Typography>
            <Typography sx={{ mt: 2, maxWidth: 530, fontFamily: ARAGON_FONTS.secondary, fontSize: `${bodySize}px`, lineHeight: 1.68, color: COLORS.muted }}>
              {active.body}
            </Typography>
          </Box>
        </Stack>
        <Box
          sx={{
            p: stacked ? `${Math.max(14, contentPadding * 0.72)}px` : `${contentPadding}px`,
            bgcolor: COLORS.navySoft,
            backgroundImage: "radial-gradient(circle at 50% 44%, rgba(204,160,116,0.08), rgba(13,23,55,0) 52%)",
            minHeight: `${artifactHeight}px`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <JourneyIllustration key={`${audience}-${activeIndex}`} artifact={active.artifact} size={{ width: illustrationWidth, height: illustrationHeight }} />
        </Box>
      </Box>
      <Box sx={{ height: 78, display: "grid", gridTemplateColumns: `repeat(${journey.length}, minmax(0, 1fr))`, alignItems: "stretch", borderTop: `1px solid ${COLORS.line}`, bgcolor: "rgba(247,246,245,0.72)" }}>
        {journey.map((step, index) => {
          const selected = activeIndex === index;
          const complete = index < activeIndex;
          return (
            <Box
              key={step.label}
              component="button"
              type="button"
              aria-current={selected ? "step" : undefined}
              aria-label={`${audience} journey step ${step.step}: ${step.label}`}
              onClick={() => activateStep(index)}
              onKeyDown={(event) => {
                if (event.key === "ArrowRight") {
                  event.preventDefault();
                  activateStep((index + 1) % journey.length);
                }
                if (event.key === "ArrowLeft") {
                  event.preventDefault();
                  activateStep((index - 1 + journey.length) % journey.length);
                }
              }}
              sx={{
                position: "relative",
                minWidth: 0,
                px: { xs: 1, sm: 1.5, md: 2.6 },
                display: "flex",
                alignItems: "center",
                textAlign: "left",
                font: "inherit",
                color: "inherit",
                border: 0,
                borderRight: index < journey.length - 1 ? `1px solid ${COLORS.line}` : "none",
                bgcolor: selected ? "rgba(204,160,116,0.08)" : "transparent",
                cursor: "pointer",
                transition: "background-color 160ms ease",
                "&:hover": { bgcolor: selected ? "rgba(204,160,116,0.11)" : "rgba(0,13,56,0.035)" },
                "&:focus-visible": { outline: `2px solid ${COLORS.accent}`, outlineOffset: -2 },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${stepNumberSize}px`, fontWeight: 800, letterSpacing: "0.08em", color: selected ? COLORS.navy : COLORS.muted, whiteSpace: "nowrap" }}>{step.step}</Typography>
                <Typography sx={{ mt: 0.35, overflow: "hidden", textOverflow: "ellipsis", fontFamily: ARAGON_FONTS.secondary, fontSize: `${stepLabelSize}px`, fontWeight: selected ? 750 : 600, color: selected ? COLORS.navy : complete ? COLORS.warmBlack : COLORS.muted, whiteSpace: "nowrap" }}>{step.label}</Typography>
              </Box>
              {selected && <Box ref={progressRef} sx={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 2, bgcolor: COLORS.accent, transformOrigin: "left center", willChange: "transform" }} />}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
function SectorCoverFlow({ sector, viewport }) {
  const wrapRef = useRef(null);
  const stageRef = useRef(null);
  const hoveredRef = useRef(null);
  const [size, setSize] = useState({ width: 1440, height: 430 });
  const [loadedImages, setLoadedImages] = useState({});
  useLayoutEffect(() => {
    const node = wrapRef.current;
    if (!node) {
      return undefined;
    }
    const observer = new ResizeObserver(([entry]) => {
      const width = Math.max(280, entry.contentRect.width);
      const height = d3.scaleLinear().domain([320, 1920]).range([270, 470]).clamp(true)(width);
      setSize({ width, height: Math.round(height) });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const visibleDepth = viewport.compact ? 2.05 : 4.05;
  const images = sector.images;
  const copies = [-1, 0, 1];
  const cards = images.flatMap((image, imageIndex) => copies.flatMap((copy) => [-1, 1].map((side) => ({
    id: `${sector.title}-${imageIndex}-${copy}-${side}`,
    birth: imageIndex + copy * images.length,
    side,
    src: image.src,
    remote: image.remote,
    fallback: sector.fallbacks[imageIndex],
    imageIndex,
  }))));
  useEffect(() => {
    const node = stageRef.current;
    if (!node) {
      return undefined;
    }
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const selection = d3.select(node).selectAll("[data-cover-card]");
    const outerWidth = d3.scaleLinear().domain([320, 1920]).range([150, 372]).clamp(true)(size.width);
    const outerHeight = size.height * 0.96;
    const innerWidth = d3.scaleLinear().domain([320, 1920]).range([52, 88]).clamp(true)(size.width);
    const innerHeight = size.height * 0.27;
    const edgeMargin = d3.scaleLinear().domain([320, 1920]).range([8, 24]).clamp(true)(size.width);
    const maxX = Math.max(0, size.width / 2 - outerWidth * 0.48 - edgeMargin);
    const xScale = d3.scalePow().exponent(1.12).domain([0, visibleDepth]).range([0, maxX]).clamp(true);
    const widthScale = d3.scaleLinear().domain([0, visibleDepth]).range([innerWidth, outerWidth]).clamp(true);
    const heightScale = d3.scaleLinear().domain([0, visibleDepth]).range([innerHeight, outerHeight]).clamp(true);
    const zScale = d3.scaleLinear().domain([0, visibleDepth]).range([-220, 180]).clamp(true);
    const rotateScale = d3.scaleLinear().domain([0, visibleDepth]).range([2, 16]).clamp(true);
    const yScale = d3.scaleLinear().domain([0, visibleDepth]).range([0, 10]).clamp(true);
    const shadeScale = d3.scaleLinear().domain([0, visibleDepth]).range([0.08, 0.32]).clamp(true);
    const fadeLimit = visibleDepth + 0.78;
    const slotDuration = d3.scaleLinear().domain([320, 1920]).range([6800, 8600]).clamp(true)(size.width);
    let phase = 0;
    let previousElapsed = null;
    const render = (currentPhase) => {
      selection.each(function () {
        const cardNode = d3.select(this);
        const birth = Number(this.dataset.birth || 0);
        const side = Number(this.dataset.side || 1);
        const age = currentPhase - birth;
        const incoming = age < 0 && age >= -1;
        const outgoing = age >= 0 && age <= fadeLimit;
        if (!incoming && !outgoing) {
          cardNode.style("opacity", 0).style("visibility", "hidden").style("pointer-events", "none");
          return;
        }
        const incomingProgress = incoming ? d3.easeCubicOut(age + 1) : 1;
        const depth = outgoing ? Math.min(visibleDepth, age) : 0;
        const edgeFade = outgoing && age > visibleDepth ? Math.max(0, 1 - (age - visibleDepth) / (fadeLimit - visibleDepth)) : 1;
        const opacity = incoming ? incomingProgress : edgeFade;
        const hovered = hoveredRef.current === this.dataset.cardId;
        const split = incoming ? 0 : depth;
        const x = side * xScale(split) - (hovered ? side * 6 : 0);
        const y = yScale(depth) - (hovered ? 7 : 0);
        const z = zScale(depth) + (hovered ? 34 : 0);
        const rotateY = side * -rotateScale(depth) * (hovered ? 0.72 : 1);
        const scale = hovered ? 1.025 : 1;
        const width = widthScale(depth) * (incoming ? 0.82 + incomingProgress * 0.18 : 1);
        const height = heightScale(depth) * (incoming ? 0.82 + incomingProgress * 0.18 : 1);
        cardNode
          .style("visibility", "visible")
          .style("width", `${width}px`)
          .style("height", `${height}px`)
          .style("opacity", opacity)
          .style("z-index", `${40 + Math.round(depth * 12) + (incoming ? 2 : 0)}`)
          .style("pointer-events", opacity < 0.35 ? "none" : "auto")
          .style("transform", `translate3d(calc(-50% + ${x}px), calc(-50% + ${y}px), ${z}px) rotateY(${rotateY}deg) scale(${scale})`);
        cardNode.select("[data-cover-shade]").style("opacity", incoming ? 0.04 + incomingProgress * 0.06 : shadeScale(depth));
      });
    };
    render(0);
    if (reducedMotion) {
      return undefined;
    }
    const timer = d3.timer((elapsed) => {
      if (document.visibilityState !== "visible") {
        previousElapsed = elapsed;
        return;
      }
      if (previousElapsed === null) {
        previousElapsed = elapsed;
        return;
      }
      const delta = Math.min(80, Math.max(0, elapsed - previousElapsed));
      previousElapsed = elapsed;
      phase = (phase + delta / slotDuration) % images.length;
      render(phase);
    });
    const handleVisibility = () => {
      previousElapsed = null;
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      timer.stop();
    };
  }, [images.length, sector.title, size.height, size.width, visibleDepth]);
  return (
    <Box ref={wrapRef} sx={{ position: "relative", height: `${size.height}px`, overflow: "hidden", bgcolor: "transparent" }}>
      <Box
        ref={stageRef}
        sx={{
          position: "absolute",
          inset: 0,
          perspective: `${Math.max(1100, size.width * 0.98)}px`,
          perspectiveOrigin: "50% 50%",
          transformStyle: "preserve-3d",
        }}
      >
        {cards.map((card) => (
          <Box
            key={card.id}
            data-cover-card
            data-card-id={card.id}
            data-birth={card.birth}
            data-side={card.side}
            tabIndex={0}
            aria-label={`${sector.title} industry image ${card.imageIndex + 1}`}
            onMouseEnter={() => {
              hoveredRef.current = card.id;
            }}
            onMouseLeave={() => {
              hoveredRef.current = null;
            }}
            onFocus={() => {
              hoveredRef.current = card.id;
            }}
            onBlur={() => {
              hoveredRef.current = null;
            }}
            sx={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transformOrigin: "50% 50%",
              overflow: "hidden",
              borderRadius: "4px",
              bgcolor: card.fallback,
              boxShadow: "0 22px 48px rgba(0,0,0,0.28)",
              willChange: "transform, width, height, opacity",
              outline: "none",
            }}
          >
            <Box
              component="img"
              src={card.src}
              alt=""
              onLoad={() => setLoadedImages((current) => ({ ...current, [card.id]: true }))}
              onError={(event) => {
                const image = event.currentTarget;
                if (image.dataset.remoteFallback === "used") {
                  image.style.display = "none";
                  return;
                }
                image.dataset.remoteFallback = "used";
                image.src = card.remote;
              }}
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
                opacity: loadedImages[card.id] ? 1 : 0,
                transition: "opacity 360ms ease",
              }}
            />
            <Box
              data-cover-shade
              sx={{
                position: "absolute",
                inset: 0,
                background: card.side < 0
                  ? "linear-gradient(90deg, rgba(0,13,56,0.02) 0%, rgba(0,13,56,0.05) 52%, rgba(0,0,0,0.48) 100%)"
                  : "linear-gradient(270deg, rgba(0,13,56,0.02) 0%, rgba(0,13,56,0.05) 52%, rgba(0,0,0,0.48) 100%)",
                pointerEvents: "none",
              }}
            />
            <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,13,56,0.01) 0%, rgba(0,13,56,0.03) 62%, rgba(0,13,56,0.18) 100%)", pointerEvents: "none" }} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function HomePage() {
  const viewport = useViewportModel();
  const [website, setWebsite] = useState("");
  const [activeSectorIndex, setActiveSectorIndex] = useState(2);
  const heroVideoRef = useRef(null);
  useEffect(() => {
    const node = heroVideoRef.current;
    if (!node) {
      return undefined;
    }
    const baseScale = viewport.heroVideoScale;
    const peakScale = Math.min(1.3, baseScale + 0.026);
    const transition = d3
      .select(node)
      .style("transform", `scale(${baseScale})`)
      .transition()
      .duration(18000)
      .ease(d3.easeSinInOut)
      .style("transform", `scale(${peakScale})`)
      .transition()
      .duration(18000)
      .style("transform", `scale(${baseScale})`);
    return () => transition.selection().interrupt();
  }, [viewport.heroVideoScale]);
  const handleSubmit = (event) => {
    event.preventDefault();
  };
  const shellSx = {
    maxWidth: `${viewport.maxWidth}px`,
    px: `${viewport.gutter}px`,
  };
  return (
    <ThemeProvider theme={aragonHomeTheme}>
      <AragonFontLoader />
      <Box
        component="main"
        sx={{
          bgcolor: COLORS.warmWhite,
          color: COLORS.warmBlack,
          fontFamily: ARAGON_FONTS.secondary,
          "& button, & input, & textarea, & select": {
            fontFamily: "inherit",
          },
          overflowX: "hidden",
        }}
      >
      <Navbar />

      <Box
        component="section"
        sx={{
          position: "relative",
          height: "100svh",
          minHeight: 0,
          overflow: "hidden",
          display: "grid",
          gridTemplateRows: `minmax(0, 1fr) ${viewport.tickerHeight}px`,
          bgcolor: COLORS.navy,
          color: COLORS.white,
          isolation: "isolate",
        }}
      >
        <Box
          ref={heroVideoRef}
          component="video"
          src={HERO_VIDEO_SRC}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: -4,
            transformOrigin: "center center",
            willChange: "transform",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: -3,
            background:
              "linear-gradient(90deg, rgba(0,13,56,0.92) 0%, rgba(0,13,56,0.76) 42%, rgba(0,13,56,0.45) 100%)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: -2,
            background:
              "linear-gradient(180deg, rgba(0,13,56,0.06) 0%, rgba(0,13,56,0.08) 58%, rgba(0,7,30,0.72) 100%)",
          }}
        />
        <Container
          maxWidth={false}
          sx={{
            ...shellSx,
            position: "relative",
            zIndex: 1,
            minHeight: 0,
            height: "100%",
            display: "grid",
            gridTemplateRows: "minmax(0, 1fr) auto",
            pt: `${viewport.navClearance}px`,
            pb: `${Math.max(8, Math.round(viewport.metricPad * 0.65))}px`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              maxWidth: { xs: "100%", md: 860 },
              transform: `translateY(${-Math.round(viewport.heroGap * 0.35)}px)`,
            }}
          >
            <Reveal delay={120} y={22}>
              <Typography
                component="h1"
                sx={{
                  m: 0,
                  fontFamily: ARAGON_FONTS.primary,
                  fontSize: `${viewport.heroTitle}px`,
                  fontWeight: 400,
                  lineHeight: 0.98,
                  letterSpacing: "-0.038em",
                  maxWidth: 880,
                }}
              >
                The Investment Bank
                <Box component="span" sx={{ display: "block", color: COLORS.accentSoft }}>
                  for Asian Businesses
                </Box>
              </Typography>
            </Reveal>
            <Reveal delay={190} y={18}>
              <Typography
                sx={{
                  mt: `${Math.max(16, viewport.heroGap * 0.75)}px`,
                  fontFamily: ARAGON_FONTS.secondary,
                  maxWidth: 660,
                  fontSize: `${viewport.heroBody}px`,
                  lineHeight: 1.58,
                  color: "rgba(255,255,255,0.82)",
                }}
              >
                A modern way to understand business value, connect with relevant buyers and manage the decisions that
                shape a transaction.
              </Typography>
            </Reveal>
            <Reveal delay={250} y={18}>
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                  mt: `${Math.max(18, viewport.heroGap)}px`,
                  width: { xs: "100%", sm: 510 },
                  maxWidth: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: `${viewport.ctaPad}px`,
                    borderRadius: `${viewport.ctaRadius}px`,
                    bgcolor: "rgba(255,255,255,0.96)",
                    border: "1px solid rgba(255,255,255,0.48)",
                    boxShadow: "0 14px 42px rgba(0,7,30,0.18)",
                    transition: "border-color 180ms ease, box-shadow 180ms ease, background-color 180ms ease",
                    "&:focus-within": {
                      bgcolor: COLORS.white,
                      borderColor: "rgba(229,198,166,0.86)",
                      boxShadow: "0 16px 46px rgba(0,7,30,0.22), 0 0 0 3px rgba(229,198,166,0.12)",
                    },
                  }}
                >
                  <TextField
                    value={website}
                    onChange={(event) => setWebsite(event.target.value)}
                    placeholder="Enter your business website"
                    variant="standard"
                    fullWidth
                    InputProps={{ disableUnderline: true }}
                    sx={{
                      minWidth: 0,
                      "& .MuiInputBase-root": {
                        height: `${viewport.ctaHeight}px`,
                        fontFamily: ARAGON_FONTS.secondary,
                        px: { xs: 1.2, sm: 1.6 },
                        fontSize: `${viewport.ctaFont}px`,
                        color: COLORS.warmBlack,
                      },
                      "& .MuiInput-root::before, & .MuiInput-root::after": {
                        display: "none",
                        borderBottom: "0 !important",
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "rgba(20,20,20,0.46)",
                        opacity: 1,
                      },
                    }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disableElevation
                    sx={{
                      flexShrink: 0,
                      minWidth: `${viewport.ctaButtonWidth}px`,
                      height: `${viewport.ctaHeight}px`,
                      borderRadius: `${Math.max(8, viewport.ctaRadius - 2)}px`,
                      bgcolor: COLORS.accent,
                      color: COLORS.navy,
                      textTransform: "none",
                      fontFamily: ARAGON_FONTS.secondary,
                      fontSize: `${viewport.ctaFont}px`,
                      fontWeight: 800,
                      letterSpacing: "-0.01em",
                      transition: "background-color 180ms ease, transform 180ms ease, box-shadow 180ms ease",
                      "&:hover": {
                        bgcolor: "#DBAF82",
                        transform: "translateY(-1px)",
                        boxShadow: "0 8px 18px rgba(61,31,40,0.14)",
                      },
                      "&:active": {
                        transform: "translateY(0)",
                      },
                    }}
                  >
                    Find Buyers
                  </Button>
                </Box>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: 12.5, color: "rgba(255,255,255,0.62)" }}>
                  Looking to buy?
                </Typography>
                <Link
                  href="#buyers"
                  color="inherit"
                  underline="always"
                  sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: 12.5, textUnderlineOffset: "3px" }}
                >
                  Join buyer network
                </Link>
              </Stack>
            </Reveal>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2,1fr)", md: "repeat(4,1fr)" },
            }}
          >
            {metrics.map((metric, index) => (
              <Box
                key={metric.label}
                sx={{
                  py: `${viewport.metricPad}px`,
                  px: { xs: index % 2 === 0 ? 0 : 2, md: index === 0 ? 0 : 3 },
                  borderRight: {
                    md: index < metrics.length - 1 ? `1px solid ${COLORS.lightLine}` : "none",
                  },
                  borderBottom: {
                    xs: index < 2 ? `1px solid ${COLORS.lightLine}` : "none",
                    md: "none",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: ARAGON_FONTS.primary,
                    fontSize: { xs: 20, md: 25 },
                    lineHeight: 1,
                    color: COLORS.white,
                  }}
                >
                  {metric.value} {metric.label}
                </Typography>
                {!viewport.shortViewport && (
                  <Typography
                    sx={{
                      mt: 0.75,
                      pr: { md: 2 },
                      fontFamily: ARAGON_FONTS.secondary,
                      fontSize: { xs: 10.5, md: 12.2 },
                      lineHeight: 1.45,
                      color: "rgba(255,255,255,0.62)",
                    }}
                  >
                    {metric.body}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Container>
        <DealTicker viewport={viewport} />
      </Box>

      <Box
        id="for-sellers"
        component="section"
        sx={{
          py: `${viewport.sectionY}px`,
          bgcolor: COLORS.warmWhite,
          scrollMarginTop: "110px",
        }}
      >
        <Container maxWidth={false} sx={shellSx}>
          <Reveal>
            <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: 12, fontWeight: 700, color: COLORS.navy, mb: 1.6 }}>
              For Sellers
            </Typography>
            <Typography
              sx={{
                fontFamily: ARAGON_FONTS.primary,
                fontSize: `${viewport.sectionTitle}px`,
                lineHeight: 1.02,
                letterSpacing: "-0.035em",
                maxWidth: 900,
              }}
            >
              Connect to hundreds of buyers
              <Box component="span" sx={{ display: "block", color: COLORS.accent }}>
                to get the best value for your business
              </Box>
            </Typography>
          </Reveal>
          <Reveal delay={120} sx={{ mt: { xs: 4, md: 5.5 } }}>
            <DealJourneySlideshow journey={sellerJourney} audience="Seller" />
          </Reveal>
        </Container>
      </Box>

      <Box
        id="buyers"
        component="section"
        sx={{
          py: `${viewport.sectionY}px`,
          bgcolor: COLORS.pale,
        }}
      >
        <Container maxWidth={false} sx={shellSx}>
          <Reveal>
            <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: 12, fontWeight: 700, color: COLORS.navy, mb: 1.6 }}>
              For Buyers
            </Typography>
            <Typography
              sx={{
                fontFamily: ARAGON_FONTS.primary,
                fontSize: `${viewport.sectionTitle}px`,
                lineHeight: 1.02,
                letterSpacing: "-0.035em",
                maxWidth: 920,
              }}
            >
              Choose and buy your future business
              <Box component="span" sx={{ display: "block", color: COLORS.accent }}>
                all in one platform
              </Box>
            </Typography>
          </Reveal>
          <Reveal delay={120} sx={{ mt: { xs: 4, md: 5.5 } }}>
            <DealJourneySlideshow journey={buyerJourney} audience="Buyer" />
          </Reveal>
        </Container>
      </Box>

      <Box
        component="section"
        sx={{
          bgcolor: COLORS.navy,
          color: COLORS.white,
          overflow: "hidden",
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            ...shellSx,
            pt: `${Math.round(viewport.sectionY * 0.78)}px`,
            pb: `${Math.round(viewport.sectionY * 0.58)}px`,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "0.58fr 1.42fr" },
              gap: { xs: 5, md: 9 },
              alignItems: "start",
            }}
          >
            <Reveal>
              <Typography
                sx={{
                  fontFamily: ARAGON_FONTS.primary,
                  fontSize: `${Math.round(viewport.sectionTitle * 0.78)}px`,
                  lineHeight: 0.96,
                  letterSpacing: "-0.035em",
                }}
              >
                Sector
                <Box component="span" sx={{ display: "block" }}>
                  expertise
                </Box>
              </Typography>
              <Typography
                sx={{
                  mt: 2.5,
                  maxWidth: 350,
                  fontFamily: ARAGON_FONTS.secondary,
                  color: "rgba(255,255,255,0.72)",
                  fontSize: 14,
                  lineHeight: 1.65,
                }}
              >
                Different businesses attract different buyers. The process should reflect those differences instead of
                treating every company the same.
              </Typography>
            </Reveal>
            <Stack divider={<Divider sx={{ borderColor: COLORS.lightLine }} />}>
              {sectors.map((sector, index) => {
                const isActive = activeSectorIndex === index;
                return (
                  <Box
                    key={sector.title}
                    component="button"
                    type="button"
                    onMouseEnter={() => setActiveSectorIndex(index)}
                    onFocus={() => setActiveSectorIndex(index)}
                    onClick={() => setActiveSectorIndex(index)}
                    sx={{
                      appearance: "none",
                      width: "100%",
                      p: 0,
                      border: 0,
                      bgcolor: "transparent",
                      color: "inherit",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "42px 1fr", sm: "48px 0.72fr 1.28fr 18px" },
                        gap: 2,
                        py: 2.15,
                        alignItems: "center",
                        transform: isActive ? "translateX(5px)" : "translateX(0)",
                        transition:
                          "transform 190ms ease, background-color 190ms ease",
                        "&:hover": {
                          transform: "translateX(5px)",
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: ARAGON_FONTS.secondary,
                          fontSize: 11,
                          color: isActive ? COLORS.accentSoft : "rgba(255,255,255,0.40)",
                          transition: "color 180ms ease",
                        }}
                      >
                        0{index + 1}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: ARAGON_FONTS.secondary,
                          fontSize: 14.5,
                          fontWeight: 650,
                          color: isActive ? COLORS.accentSoft : COLORS.white,
                          transition: "color 180ms ease",
                        }}
                      >
                        {sector.title}
                      </Typography>
                      <Typography
                        sx={{
                          gridColumn: { xs: "2 / 3", sm: "auto" },
                          fontFamily: ARAGON_FONTS.secondary,
                          color: isActive ? "rgba(255,255,255,0.72)" : "rgba(255,255,255,0.50)",
                          fontSize: 11.5,
                          lineHeight: 1.5,
                          transition: "color 180ms ease",
                        }}
                      >
                        {sector.description}
                      </Typography>
                      <Box
                        sx={{
                          display: { xs: "none", sm: "block" },
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          bgcolor: isActive ? COLORS.accentSoft : "transparent",
                          border: isActive ? "none" : "1px solid rgba(255,255,255,0.24)",
                          transition: "background-color 180ms ease, border-color 180ms ease",
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Container>
        <Box sx={{ width: "100%", pb: { xs: 1.5, md: 2.5 } }}>
          <SectorCoverFlow sector={sectors[activeSectorIndex]} viewport={viewport} />
        </Box>
      </Box>

      <Box
        component="section"
        sx={{
          py: `${viewport.sectionY}px`,
          bgcolor: COLORS.warmWhite,
        }}
      >
        <Container maxWidth={false} sx={shellSx}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "0.82fr 1.18fr" },
              gap: { xs: 5, md: 8 },
              alignItems: "end",
              mb: { xs: 4, md: 5.5 },
            }}
          >
            <Reveal>
              <Typography
                sx={{
                  fontFamily: ARAGON_FONTS.primary,
                  fontSize: `${Math.round(viewport.sectionTitle * 0.86)}px`,
                  lineHeight: 1,
                  letterSpacing: "-0.035em",
                  maxWidth: 560,
                }}
              >
                Intelligence behind
                <Box component="span" sx={{ display: "block" }}>
                  every serious deal
                </Box>
              </Typography>
            </Reveal>
            <Reveal delay={100}>
              <Typography sx={{ maxWidth: 480, color: COLORS.muted, fontFamily: ARAGON_FONTS.secondary, fontSize: 14, lineHeight: 1.65 }}>
                Choose the side of the transaction that matches your objective. Aragon will take you into the corresponding
                registration flow with that role already selected.
              </Typography>
            </Reveal>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2.5,
            }}
          >
            {[
              {
                title: "Seller",
                body: "Understand value, prepare your business and connect with qualified buyers.",
                cta: "Register as Seller →",
                to: "/auth/register?role=seller",
                backgroundImage: SELLER_CTA_IMAGE,
                backgroundPosition: "center center",
              },
              {
                title: "Buyer",
                body: "Discover businesses that match your acquisition criteria and transaction priorities.",
                cta: "Register as Buyer →",
                to: "/auth/register?role=buyer",
                backgroundImage: BUYER_CTA_IMAGE,
                backgroundPosition: "center center",
              },
            ].map((card, index) => (
              <Reveal key={card.title} delay={index * 110}>
                <Box
                  component={RouterLink}
                  to={card.to}
                  aria-label={`${card.cta.replace(" →", "")}: ${card.body}`}
                  sx={{
                    position: "relative",
                    minHeight: `${viewport.intelligenceCardHeight}px`,
                    p: `${viewport.intelligenceCardPadding}px`,
                    borderRadius: "7px",
                    color: COLORS.white,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                    overflow: "hidden",
                    textDecoration: "none",
                    isolation: "isolate",
                    transition: "transform 200ms ease, box-shadow 200ms ease",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      zIndex: -2,
                      backgroundImage: `url("${card.backgroundImage}")`,
                      backgroundSize: "cover",
                      backgroundPosition: card.backgroundPosition,
                      backgroundRepeat: "no-repeat",
                      transform: `scale(${viewport.intelligenceImageScale})`,
                      transformOrigin: "center center",
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      zIndex: -1,
                      background: "linear-gradient(180deg, rgba(0,13,56,0.08) 12%, rgba(0,13,56,0.76) 100%)",
                    },
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 18px 50px rgba(0,13,56,0.16)",
                    },
                    "&:hover .intelligence-cta": { color: COLORS.accentSoft },
                    "&:focus-visible": {
                      outline: `2px solid ${COLORS.accentSoft}`,
                      outlineOffset: "3px",
                    },
                  }}
                >
                  <Typography sx={{ fontFamily: ARAGON_FONTS.primary, fontSize: `${viewport.intelligenceCardTitle}px`, lineHeight: 1.05 }}>
                    {card.title}
                  </Typography>
                  <Typography sx={{ mt: 1.2, maxWidth: 480, fontFamily: ARAGON_FONTS.secondary, fontSize: 13.5, lineHeight: 1.55, opacity: 0.86 }}>
                    {card.body}
                  </Typography>
                  <Typography
                    className="intelligence-cta"
                    component="span"
                    sx={{
                      mt: 2.5,
                      width: "fit-content",
                      color: COLORS.white,
                      fontFamily: ARAGON_FONTS.secondary,
                      fontSize: 12.5,
                      fontWeight: 700,
                      transition: "color 180ms ease",
                    }}
                  >
                    {card.cta}
                  </Typography>
                </Box>
              </Reveal>
            ))}
          </Box>
        </Container>
      </Box>

      <Box
        id="faq"
        component="section"
        sx={{
          py: `${Math.round(viewport.sectionY * 0.9)}px`,
          bgcolor: COLORS.warmWhite,
          scrollMarginTop: "110px",
          borderTop: `1px solid ${COLORS.line}`,
        }}
      >
        <Container maxWidth={false} sx={shellSx}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "0.58fr 1.42fr" },
              gap: { xs: 5, md: 9 },
            }}
          >
            <Reveal>
              <Typography
                sx={{
                  fontFamily: ARAGON_FONTS.primary,
                  fontSize: `${Math.round(viewport.sectionTitle * 0.84)}px`,
                  lineHeight: 0.98,
                  letterSpacing: "-0.035em",
                }}
              >
                Frequently
                <Box component="span" sx={{ display: "block", color: COLORS.accent }}>
                  asked.
                </Box>
              </Typography>
              <Typography sx={{ mt: 2.2, fontFamily: ARAGON_FONTS.secondary, fontSize: 12.5, color: COLORS.muted }}>
                Have a more specific question about the process?
              </Typography>
            </Reveal>
            <Box>
              {faqs.map((item) => (
                <Accordion
                  key={item.question}
                  disableGutters
                  elevation={0}
                  square
                  sx={{
                    bgcolor: "transparent",
                    borderTop: `1px solid ${COLORS.line}`,
                    "&:last-of-type": { borderBottom: `1px solid ${COLORS.line}` },
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <Typography component="span" sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: 20, fontWeight: 400 }}>
                        +
                      </Typography>
                    }
                    sx={{
                      px: 0,
                      minHeight: 64,
                    }}
                  >
                    <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: 13.5, fontWeight: 500 }}>{item.question}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0, pt: 0, pb: 2.8 }}>
                    <Typography sx={{ maxWidth: 700, color: COLORS.muted, fontFamily: ARAGON_FONTS.secondary, fontSize: 13, lineHeight: 1.65 }}>
                      {item.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      <Box
        component="footer"
        sx={{
          py: { xs: 6, md: 7 },
          bgcolor: COLORS.wine,
          color: COLORS.white,
        }}
      >
        <Container maxWidth={false} sx={shellSx}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "1.6fr repeat(4, 1fr)" },
              gap: { xs: 4, md: 5 },
            }}
          >
            <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
              <Stack direction="row" alignItems="center" spacing={1.2}>
                <Box
                  component="img"
                  src={LOGO_SRC}
                  alt="Aragon"
                  sx={{ width: 30, height: 30, objectFit: "contain" }}
                />
                <Typography sx={{ fontFamily: ARAGON_FONTS.primary, fontSize: 20 }}>Aragon</Typography>
              </Stack>
              <Typography
                sx={{
                  mt: 1.5,
                  maxWidth: 300,
                  color: "rgba(255,255,255,0.62)",
                  fontFamily: ARAGON_FONTS.secondary,
                  fontSize: 11.5,
                  lineHeight: 1.55,
                }}
              >
                A modern valuation and transaction intelligence experience for Asian businesses.
              </Typography>
            </Box>
            {[
              ["Services", ["Sell a Business", "Buyer Network", "Advisory"]],
              ["Tools", ["Valuation", "Comparables", "Market Intelligence"]],
              ["Company", ["About", "Careers", "Media"]],
              ["Legal", ["Terms", "Privacy", "Contact"]],
            ].map(([title, links]) => (
              <Box key={title}>
                <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: 11.5, fontWeight: 700, mb: 1.5 }}>{title}</Typography>
                <Stack spacing={1}>
                  {links.map((label) => (
                    <Link
                      key={label}
                      href="#"
                      underline="none"
                      color="inherit"
                      sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: 11, color: "rgba(255,255,255,0.58)" }}
                    >
                      {label}
                    </Link>
                  ))}
                </Stack>
              </Box>
            ))}
          </Box>
          <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.12)" }} />
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1.3}>
            <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: 10.5, color: "rgba(255,255,255,0.46)" }}>
              © 2026 Aragon. Visual mock only.
            </Typography>
            <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: 10.5, color: "rgba(255,255,255,0.46)" }}>
              Mock deal data · No production claims
            </Typography>
          </Stack>
        </Container>
      </Box>
      </Box>
    </ThemeProvider>
  );
}

export default HomePage;
